import { BREAKPOINT_ORDER, type Breakpoint } from './theme/breakpoints';
import type { ResponsiveValue, Primitive } from './responsiveSSR';

type NormaliseFn<T extends Primitive> = (value: T) => string;
type CssVarStyles = React.CSSProperties & Record<`--${string}`, string>;

/**
 *
 * Convert a responsive value into CSS variables for each breakpoint.
 * Use this to set CSS variables that can be consumed in your styles.
 * Styles will have CSS variables like:
 * --box-width-base, --box-width-sm, --box-width-md, etc.
 * The `normalise` function is used to convert the raw value into a string
 * suitable for CSS (e.g. adding units).
 *
 * Example usage:
 * ```ts
 * const styles = responsiveToCssVars(width, {
 *   varPrefix: '--box-width',
 *   normalise: (val) => typeof val === 'number' ? `${val}px` : val,
 * });
 * ```
 * This will produce an object like:
 * {
 *  '--box-width-base': '100px',
 * '--box-width-md': '200px',
 * }
 *
 * You can then spread this object into your component's style prop.
 *
 * Example CSS usage:
 * ```css
 * .box {
 *  width: var(--box-width-base);
 * }
 * @media (min-width: 768px) {
 * .box {
 * width: var(--box-width-md);
 * }
 * }
 * ```
 *
 * This allows you to create responsive designs using CSS variables
 * that adapt at different breakpoints.
 *
 * @param value
 * @param options
 * @returns
 */
export function responsiveToCssVars<T extends Primitive>(
  value: ResponsiveValue<T> | null | undefined,
  options: {
    varPrefix: `--${string}`; // e.g. '--box-width', '--grid-template-columns'
    normalise: NormaliseFn<T>;
  },
): React.CSSProperties {
  const { varPrefix, normalise } = options;
  const styles: CssVarStyles = {} as CssVarStyles;

  // No value → return empty styles
  if (value == null) {
    return styles;
  }

  // Helper to assign a CSS variable for a given breakpoint
  const assign = (bp: Breakpoint, raw: T) => {
    const resolved = normalise(raw);
    if (!resolved) return;

    const suffix = bp === 'base' ? 'base' : bp;
    const cssVarName = `${varPrefix}-${suffix}` as `--${string}`;
    styles[cssVarName] = resolved;
  };

  // Scalar → treat as base
  if (typeof value !== 'object' || Array.isArray(value)) {
    assign('base', value as T);
    return styles;
  }

  // Object form: iterate in your canonical breakpoint order
  for (const bp of BREAKPOINT_ORDER) {
    const v = (value as Partial<Record<Breakpoint, T>>)[bp];
    if (v != null) assign(bp, v);
  }

  return styles;
}
